<template>
  <div class="deletion-gate">
    <h3>{{ $t("admin.contracts.deletionGate.title") }}</h3>
    <p>{{ $t("admin.contracts.deletionGate.subtitle") }}</p>

    <v-alert
      :type="stateTone"
      density="compact"
      class="mb-3"
    >
      {{ stateMessage }}
    </v-alert>

    <v-switch
      :model-value="company.deletion_protected"
      color="primary"
      density="comfortable"
      hide-details
      :loading="busy === 'protection'"
      :label="$t('admin.contracts.deletionGate.protected')"
      @update:model-value="toggleProtection"
    />
    <p class="deletion-gate__hint">{{ $t("admin.contracts.deletionGate.protectedHint") }}</p>

    <!-- The retention story only exists once the tenant is actually on the
         automatic path. For an active company it would be a list of obstacles to
         something nobody asked for, so it stays hidden. -->
    <template v-if="inRetentionWindow">
      <ul v-if="blockingReasons.length" class="deletion-gate__reasons">
        <li v-for="reason in blockingReasons" :key="reason">{{ reasonLabel(reason) }}</li>
      </ul>

      <div v-if="approvalActive" class="deletion-gate__approved">
        <p>{{ $t("admin.contracts.deletionGate.approvedAt", { date: formatDate(company.deletion_approved_at) }) }}</p>
        <v-btn variant="tonal" size="small" :loading="busy === 'revoke'" @click="revoke">
          {{ $t("admin.contracts.deletionGate.revoke") }}
        </v-btn>
      </div>

      <v-btn
        v-else-if="canApprove"
        color="error"
        variant="tonal"
        size="small"
        class="mt-2"
        @click="dialogOpen = true"
      >
        {{ $t("admin.contracts.deletionGate.approve") }}
      </v-btn>
    </template>

    <!-- The manual path, for test or abandoned tenants that should not wait out
         a six-month retention window. -->
    <div class="deletion-gate__manual">
      <v-btn
        color="error"
        variant="outlined"
        size="small"
        :disabled="!manualDeleteAllowed"
        @click="deleteDialogOpen = true"
      >
        {{ $t("admin.contracts.manualDelete.action") }}
      </v-btn>
      <p v-if="manualBlockingReasons.length" class="deletion-gate__hint">
        {{ manualBlockingReasons.map(reasonLabel).join(" ") }}
      </p>
    </div>

    <AdminCompanyDeleteDialog
      v-model="deleteDialogOpen"
      :company="company"
      @deleted="$emit('deleted', $event)"
    />

    <v-dialog v-model="dialogOpen" max-width="520" persistent>
      <v-card>
        <v-card-title>{{ $t("admin.contracts.deletionGate.dialogTitle") }}</v-card-title>
        <v-card-text>
          <p class="mb-3">{{ $t("admin.contracts.deletionGate.dialogBody", { company: company.company_name }) }}</p>
          <v-alert type="error" density="compact" class="mb-4">
            {{ $t("admin.contracts.deletionGate.dialogWarning") }}
          </v-alert>
          <v-alert v-if="errorMessage" type="error" density="compact" class="mb-4">
            {{ errorMessage }}
          </v-alert>
          <v-text-field
            v-model="confirmationName"
            :label="$t('admin.contracts.deletionGate.confirmationLabel')"
            :placeholder="company.company_name"
            autocomplete="off"
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="busy === 'approve'" @click="closeDialog">
            {{ $t("app.actions.cancel") }}
          </v-btn>
          <v-btn color="error" variant="flat" :loading="busy === 'approve'" :disabled="!confirmed" @click="approve">
            {{ $t("admin.contracts.deletionGate.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AdminCompanyDeleteDialog from "./AdminCompanyDeleteDialog.vue";
import {
  approveCompanyDeletion,
  formatAdminContractDate,
  revokeCompanyDeletionApproval,
  setCompanyDeletionProtection,
} from "./adminContractsApi.mjs";

const normalizeName = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();

export default {
  name: "AdminDeletionGateSection",
  components: { AdminCompanyDeleteDialog },
  props: {
    company: { type: Object, required: true },
  },
  emits: ["changed", "deleted"],
  data() {
    return {
      dialogOpen: false,
      deleteDialogOpen: false,
      confirmationName: "",
      busy: "",
      errorMessage: "",
    };
  },
  computed: {
    lifecycle() {
      return this.company?.lifecycle || {};
    },
    approvalActive() {
      return Boolean(this.company?.deletion_approved_at);
    },
    canApprove() {
      return Boolean(this.lifecycle.can_approve) && !this.company?.deletion_protected;
    },
    blockingReasons() {
      return Array.isArray(this.lifecycle.blocked_by) ? this.lifecycle.blocked_by : [];
    },
    // An active tenant has nothing scheduled, so the retention explanation would
    // only be noise; it appears the moment the tenant enters read-only.
    inRetentionWindow() {
      return Boolean(this.lifecycle.in_retention_window);
    },
    manualDeleteAllowed() {
      return Boolean(this.lifecycle.manual_delete_allowed);
    },
    manualBlockingReasons() {
      return Array.isArray(this.lifecycle.manual_delete_blocked_by)
        ? this.lifecycle.manual_delete_blocked_by
        : [];
    },
    // Green only when a deletion could really happen; that state must never look
    // routine, hence the explicit warning tone everywhere else.
    stateTone() {
      if (this.company?.deletion_protected) return "success";
      if (this.lifecycle.deletable) return "error";
      return "info";
    },
    stateMessage() {
      if (this.company?.deletion_protected) return this.$t("admin.contracts.deletionGate.stateProtected");
      if (this.lifecycle.deletable) return this.$t("admin.contracts.deletionGate.stateDeletable");
      if (this.approvalActive && !this.lifecycle.approval_valid) {
        return this.$t("admin.contracts.deletionGate.stateStale");
      }
      return this.$t("admin.contracts.deletionGate.stateSafe");
    },
    // The exact name is required: a keyword would be as easy to type on the
    // wrong row of the list.
    confirmed() {
      return (
        Boolean(this.confirmationName.trim()) &&
        normalizeName(this.confirmationName) === normalizeName(this.company?.company_name)
      );
    },
  },
  methods: {
    formatDate: formatAdminContractDate,
    reasonLabel(reason) {
      const key = `admin.contracts.deletionGate.reasons.${reason}`;
      const translated = this.$t(key);
      return translated === key ? reason : translated;
    },
    closeDialog() {
      this.dialogOpen = false;
      this.confirmationName = "";
      this.errorMessage = "";
    },
    async run(kind, action) {
      this.busy = kind;
      this.errorMessage = "";
      try {
        const { data } = await action();
        this.$emit("changed", data?.company || null);
        return true;
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || this.$t("admin.contracts.deletionGate.errors.failed");
        return false;
      } finally {
        this.busy = "";
      }
    },
    async approve() {
      if (!this.confirmed) return;
      const ok = await this.run("approve", () =>
        approveCompanyDeletion(this.company.company_id, this.confirmationName.trim())
      );
      if (ok) this.closeDialog();
    },
    revoke() {
      return this.run("revoke", () => revokeCompanyDeletionApproval(this.company.company_id));
    },
    toggleProtection(value) {
      return this.run("protection", () =>
        setCompanyDeletionProtection(this.company.company_id, Boolean(value))
      );
    },
  },
};
</script>

<style scoped>
.deletion-gate { padding-top: 20px; margin-top: 20px; border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08); text-align: start; }
.deletion-gate h3 { margin: 0 0 4px; color: var(--app-text-strong); font-size: 1rem; }
.deletion-gate > p { margin: 0 0 12px; color: var(--app-text-default); font-size: 0.78rem; opacity: 0.7; line-height: 1.45; }
.deletion-gate__hint { margin: 4px 0 12px; color: var(--app-text-default); font-size: 0.74rem; opacity: 0.66; }
.deletion-gate__reasons { margin: 0 0 12px; padding-left: 18px; color: var(--app-text-default); font-size: 0.76rem; opacity: 0.75; }
.deletion-gate__reasons li { margin-bottom: 2px; }
.deletion-gate__approved { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.deletion-gate__approved p { margin: 0; color: var(--app-text-strong); font-size: 0.82rem; }
.deletion-gate__manual { padding-top: 14px; margin-top: 14px; border-top: 1px dashed rgba(var(--v-theme-on-surface), 0.12); }
.deletion-gate__manual .deletion-gate__hint { margin: 8px 0 0; }
</style>
